<script setup lang="ts">
import { computed } from "vue";
import { _battle } from "../ui/battle";
import { Pet } from "../core/pet";

const battle = _battle;

const visible = computed(() => battle.phase === "draft");

const stageText = computed(() => {
    if (battle.draftStage === "ban") {
        const finishedSide =
            battle.draftDrafter === "left" ? "被挑战方" : "挑战方";
        return `ban 阶段（${finishedSide} 已完成）：请【${sideText(
            battle.draftDrafter
        )}】在精灵池中 ban 掉 1 只精灵`;
    }
    return `pick 阶段：请【${sideText(
        battle.draftDrafter
    )}】pick 精灵（本轮还可选 ${battle.draftRemainPick} 只）`;
});

const poolOrder = computed(() =>
    [...battle.draftPool, ...battle.draftBanned, ...battle.draftLeft, ...battle.draftRight].sort(
        (a, b) => a.序号 - b.序号
    )
);

function poolState(pet: Pet): "available" | "banned" | "left" | "right" {
    if (battle.draftBanned.includes(pet)) return "banned";
    if (battle.draftLeft.includes(pet)) return "left";
    if (battle.draftRight.includes(pet)) return "right";
    return "available";
}

function poolLabel(pet: Pet): string {
    switch (poolState(pet)) {
        case "banned":
            return "已ban";
        case "left":
            return "挑战方";
        case "right":
            return "被挑战方";
        default:
            return "可选";
    }
}

function onPoolClick(pet: Pet) {
    if (poolState(pet) !== "available") return;
    battle.draftSelect(battle.draftDrafter, pet);
}

function sideText(side: "left" | "right"): string {
    return side === "left" ? "挑战方" : "被挑战方";
}
</script>

<template>
    <div v-if="visible" class="draft-overlay">
        <div class="draft-panel">
            <div class="draft-title">精灵池选择</div>
            <div class="draft-stage">{{ stageText }}</div>

            <div class="draft-picks">
                <div class="pick-col left-col">
                    <div class="pick-col-title">挑战方（左）已选</div>
                    <div class="pick-pets">
                        <div
                            v-for="pet in battle.draftLeft"
                            :key="pet.序号"
                            class="pick-pet"
                        >
                            <img :src="pet.getHeadImgUrl()" />
                            <span>{{ pet.名字 }}</span>
                        </div>
                        <div v-if="battle.draftLeft.length === 0" class="empty">未选</div>
                    </div>
                </div>
                <div class="pick-col right-col">
                    <div class="pick-col-title">被挑战方（右）已选</div>
                    <div class="pick-pets">
                        <div
                            v-for="pet in battle.draftRight"
                            :key="pet.序号"
                            class="pick-pet"
                        >
                            <img :src="pet.getHeadImgUrl()" />
                            <span>{{ pet.名字 }}</span>
                        </div>
                        <div v-if="battle.draftRight.length === 0" class="empty">未选</div>
                    </div>
                </div>
            </div>

            <div class="draft-pool">
                <div
                    v-for="pet in poolOrder"
                    :key="pet.序号"
                    class="pool-slot"
                    :class="[`state-${poolState(pet)}`, { disabled: poolState(pet) !== 'available' }]"
                >
                    <button
                        class="pool-btn"
                        :disabled="poolState(pet) !== 'available'"
                        @click="onPoolClick(pet)"
                    >
                        <img :src="pet.getHeadImgUrl()" />
                    </button>
                    <span class="pool-tag">{{ poolLabel(pet) }}</span>
                    <span class="pool-name">{{ pet.名字 }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.draft-overlay {
    position: absolute;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(5, 5, 12, 0.82);
    backdrop-filter: blur(calc(var(--ui-unit) * 10));
}

.draft-panel {
    width: min(92%, calc(var(--ui-unit) * 960));
    max-height: 90%;
    overflow-y: auto;
    padding: calc(var(--ui-unit) * 20) calc(var(--ui-unit) * 24);
    background: rgba(16, 16, 30, 0.95);
    border: calc(var(--ui-unit) * 1) solid rgba(255, 215, 0, 0.25);
    border-radius: calc(var(--ui-unit) * 14);
    box-shadow: 0 0 calc(var(--ui-unit) * 40) rgba(255, 215, 0, 0.12);
}

.draft-title {
    text-align: center;
    font-size: calc(var(--ui-unit) * 20);
    font-weight: 700;
    color: #ffd700;
    letter-spacing: calc(var(--ui-unit) * 2);
}

.draft-stage {
    text-align: center;
    margin: calc(var(--ui-unit) * 10) 0 calc(var(--ui-unit) * 16) 0;
    padding: calc(var(--ui-unit) * 6) calc(var(--ui-unit) * 12);
    color: #e8e8f0;
    background: rgba(255, 215, 0, 0.08);
    border: calc(var(--ui-unit) * 1) solid rgba(255, 215, 0, 0.2);
    border-radius: calc(var(--ui-unit) * 8);
    font-size: calc(var(--ui-unit) * 13);
}

.draft-picks {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: calc(var(--ui-unit) * 12);
    margin-bottom: calc(var(--ui-unit) * 16);
}

.pick-col {
    padding: calc(var(--ui-unit) * 10);
    background: rgba(30, 30, 50, 0.6);
    border-radius: calc(var(--ui-unit) * 10);
}

.right-col {
    text-align: right;
}

.pick-col-title {
    font-size: calc(var(--ui-unit) * 12);
    font-weight: 600;
    color: #a0a0b0;
    margin-bottom: calc(var(--ui-unit) * 8);
}

.pick-pets {
    display: flex;
    flex-wrap: wrap;
    gap: calc(var(--ui-unit) * 8);
    justify-content: flex-start;
}

.right-col .pick-pets {
    justify-content: flex-end;
}

.pick-pet {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: calc(var(--ui-unit) * 2);
    font-size: calc(var(--ui-unit) * 10);
    color: #d0d0e0;
}

.pick-pet img {
    width: calc(var(--ui-unit) * 40);
    height: calc(var(--ui-unit) * 40);
    border-radius: calc(var(--ui-unit) * 8);
    object-fit: cover;
    border: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.15);
}

.empty {
    color: #6b7280;
    font-size: calc(var(--ui-unit) * 11);
    padding: calc(var(--ui-unit) * 8) 0;
}

.draft-pool {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(calc(var(--ui-unit) * 88), 1fr));
    gap: calc(var(--ui-unit) * 10);
}

.pool-slot {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: calc(var(--ui-unit) * 3);
}

.pool-btn {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: calc(var(--ui-unit) * 64);
    height: calc(var(--ui-unit) * 64);
    padding: 0;
    background: rgba(30, 30, 45, 0.9);
    border: calc(var(--ui-unit) * 2) solid rgba(255, 255, 255, 0.12);
    border-radius: calc(var(--ui-unit) * 12);
    cursor: pointer;
    transition: all 0.2s ease;
}

.pool-btn img {
    width: calc(var(--ui-unit) * 52);
    height: calc(var(--ui-unit) * 52);
    border-radius: calc(var(--ui-unit) * 8);
    object-fit: cover;
}

.pool-btn:hover:not(:disabled) {
    border-color: #ffd700;
    box-shadow: 0 0 calc(var(--ui-unit) * 16) rgba(255, 215, 0, 0.3);
    transform: translateY(calc(var(--ui-unit) * -2));
}

.pool-slot.disabled .pool-btn {
    cursor: not-allowed;
    opacity: 0.45;
}

.pool-slot.state-banned .pool-btn {
    border-color: rgba(239, 68, 68, 0.6);
}

.pool-slot.state-left .pool-btn {
    border-color: #3b82f6;
    box-shadow: 0 0 calc(var(--ui-unit) * 12) rgba(59, 130, 246, 0.3);
}

.pool-slot.state-right .pool-btn {
    border-color: #22c55e;
    box-shadow: 0 0 calc(var(--ui-unit) * 12) rgba(34, 197, 94, 0.3);
}

.pool-tag {
    font-size: calc(var(--ui-unit) * 10);
    font-weight: 600;
    padding: calc(var(--ui-unit) * 1) calc(var(--ui-unit) * 8);
    border-radius: calc(var(--ui-unit) * 8);
}

.state-available .pool-tag {
    color: #ffd700;
    background: rgba(255, 215, 0, 0.12);
}

.state-banned .pool-tag {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.12);
}

.state-left .pool-tag {
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.12);
}

.state-right .pool-tag {
    color: #22c55e;
    background: rgba(34, 197, 94, 0.12);
}

.pool-name {
    font-size: calc(var(--ui-unit) * 10);
    color: #c8c8d8;
    text-align: center;
    line-height: 1.3;
}


</style>
